"use client"

// ImageLightbox: an inline image thumbnail that expands to a full-screen modal
// on click, with a "Volver" (close) control and a "Descargar" control in the top
// bar. Built on the Radix Dialog primitive so it gets focus trap, Escape-to-
// close, scroll lock, and click-outside-to-close for free.

import * as DialogPrimitive from "@radix-ui/react-dialog"
import { ArrowLeft, Download } from "lucide-react"
import type { ReactNode } from "react"
import { cn } from "@/lib/utils"

interface ImageLightboxProps {
  /** Image URL (the inline plot/figure; usually a presigned S3 PNG). */
  src: string
  /** Accessible alt / title text (the deliverable name). */
  alt: string
  /** Download filename; defaults to `alt`. */
  downloadName?: string
  /** Classes for the inline thumbnail image. */
  thumbnailClassName?: string
  /** Forwarded from the caller to track load/error state on the thumbnail. */
  onLoad?: () => void
  /** Forwarded from the caller to track load/error state on the thumbnail. */
  onError?: () => void
}

/**
 * Clickable thumbnail that opens a full-screen image viewer.
 *
 * @param src - the image URL.
 * @param alt - accessible alt/title text.
 * @param downloadName - download filename (defaults to alt).
 * @param thumbnailClassName - classes for the inline thumbnail.
 * @param onLoad - thumbnail load callback.
 * @param onError - thumbnail error callback.
 */
export function ImageLightbox({
  src,
  alt,
  downloadName,
  thumbnailClassName,
  onLoad,
  onError,
}: ImageLightboxProps): ReactNode {
  const name = downloadName ?? alt

  return (
    <DialogPrimitive.Root>
      <DialogPrimitive.Trigger asChild>
        <button
          type="button"
          aria-label={`Ampliar imagen: ${name}`}
          className="block w-full cursor-zoom-in rounded focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
        >
          <img
            src={src}
            alt={alt}
            loading="lazy"
            className={cn("max-w-full rounded border border-border", thumbnailClassName)}
            onLoad={onLoad}
            onError={onError}
          />
        </button>
      </DialogPrimitive.Trigger>

      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0" />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0 data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95"
        >
          {/* Accessible title (visually hidden). */}
          <DialogPrimitive.Title className="sr-only">{name}</DialogPrimitive.Title>

          {/* Clicking the empty backdrop area closes the viewer. */}
          <DialogPrimitive.Close
            aria-label="Cerrar"
            className="absolute inset-0 cursor-zoom-out outline-none"
          />

          {/* Top toolbar: Volver (left) and Descargar (right). */}
          <div className="absolute inset-x-0 top-0 z-20 flex items-center justify-between gap-2 p-4">
            <DialogPrimitive.Close asChild>
              <button
                type="button"
                className="inline-flex items-center gap-2 rounded-full bg-card/90 px-3.5 py-2 text-sm font-medium text-foreground shadow-md backdrop-blur transition-colors hover:bg-card focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
              >
                <ArrowLeft className="size-4" aria-hidden="true" />
                <span>Volver</span>
              </button>
            </DialogPrimitive.Close>

            <a
              href={src}
              download={name}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-primary px-3.5 py-2 text-sm font-medium text-primary-foreground shadow-md transition-colors hover:bg-brand-primary-dark focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
            >
              <Download className="size-4" aria-hidden="true" />
              <span>Descargar</span>
            </a>
          </div>

          {/* The image. Sits above the backdrop close so clicking it keeps the
              viewer open; only the surrounding empty space closes. */}
          <img
            src={src}
            alt={alt}
            className="relative z-10 max-h-[90vh] max-w-[92vw] rounded object-contain shadow-2xl"
          />
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
